// Voyo design-system primitives for @flight/web.
// Sunset palette: orange #E8441A, dark bg #0A0F1C, surface #131C30.
// Mirrors VoyoButton / VoyoChip / VoyoTag / VoyoCard from design-system.jsx
// plus Modal (Radix Dialog), EmptyState, ErrorState, Spinner, NotYetWiredToast.

import * as React from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { toast } from 'sonner';
import { X } from 'lucide-react';
import { cn } from '@/lib/cn';

// ─── TOKENS ────────────────────────────────────────────────────────────────
export const T = {
  bg:      '#0A0F1C',
  bg2:     '#0F1628',
  surface: '#131C30',
  surface2:'#1A2540',
  ink:     '#F4F3F0',
  inkDim:  '#8C98B0',
  inkLow:  '#4E5E78',
  primary: '#E8441A',
  accent:  '#4B8EDB',
  yellow:  '#F5A623',
  line:    'rgba(255,255,255,0.07)',
  lineHi:  'rgba(255,255,255,0.14)',
} as const;

// ─── BUTTON ─────────────────────────────────────────────────────────────────
type ButtonVariant = 'primary' | 'accent' | 'ghost' | 'soft' | 'flame';
type ButtonSize    = 'sm' | 'md' | 'lg' | 'xl';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  full?: boolean;
  loading?: boolean;
  icon?: React.ReactNode;
}

const BUTTON_SIZE: Record<ButtonSize, string> = {
  sm: 'h-9 px-3.5 text-sm gap-1.5',
  md: 'h-12 px-5 text-sm gap-2',
  lg: 'h-14 px-6 text-base gap-2',
  xl: 'h-16 px-7 text-base gap-2',
};

const BUTTON_VARIANT: Record<ButtonVariant, string> = {
  primary: 'bg-[#F4F3F0] text-[#0A0F1C] border-transparent hover:opacity-90',
  accent:  'bg-[#E8441A] text-white border-transparent shadow-[0_6px_16px_#E8441A33] hover:bg-[#C53A16]',
  ghost:   'bg-transparent text-[#F4F3F0] border-[rgba(255,255,255,0.14)] hover:bg-[rgba(255,255,255,0.08)]',
  soft:    'bg-[rgba(255,255,255,0.08)] text-[#F4F3F0] border-transparent hover:bg-[rgba(255,255,255,0.12)]',
  flame:   'bg-[#FF5A3C] text-white border-transparent hover:bg-[#E84B30]',
};

export function Button({
  variant = 'primary',
  size = 'md',
  full,
  loading,
  icon,
  children,
  className,
  disabled,
  ...props
}: ButtonProps) {
  return (
    <button
      {...props}
      disabled={disabled || loading}
      className={cn(
        'inline-flex items-center justify-center rounded-full font-semibold border',
        'transition-all duration-150 cursor-pointer select-none',
        'active:scale-[0.97] disabled:opacity-60 disabled:cursor-not-allowed',
        BUTTON_SIZE[size],
        BUTTON_VARIANT[variant],
        full && 'w-full',
        className,
      )}
    >
      {loading ? (
        <Spinner className="mr-2" />
      ) : icon ? (
        <span className="flex-shrink-0">{icon}</span>
      ) : null}
      {children}
    </button>
  );
}

// ─── INPUT ───────────────────────────────────────────────────────────────────
interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  hint?: React.ReactNode;
  error?: string;
}

export function Input({ label, hint, error, className, ...props }: InputProps) {
  return (
    <div className="flex flex-col gap-1.5">
      {label && (
        <label className="text-[10px] font-bold tracking-[1.5px] uppercase text-[#8C98B0]">
          {label}
        </label>
      )}
      <div className="relative flex items-center">
        <input
          {...props}
          className={cn(
            'w-full h-12 rounded-xl px-4',
            'bg-[#0A0F1C] text-[#F4F3F0] border border-[rgba(255,255,255,0.14)]',
            'placeholder:text-[#4E5E78] text-sm font-medium',
            'focus:outline-none focus:border-[#E8441A] transition-colors',
            error && 'border-red-500',
            className,
          )}
        />
        {hint && (
          <span className="absolute right-4 text-xs text-[#E8441A] font-semibold">{hint}</span>
        )}
      </div>
      {error && <span className="text-xs text-red-400">{error}</span>}
    </div>
  );
}

// ─── LABEL ───────────────────────────────────────────────────────────────────
export function Label({ children, className, htmlFor }: {
  children: React.ReactNode;
  className?: string;
  htmlFor?: string;
}) {
  return (
    <label
      htmlFor={htmlFor}
      className={cn(
        'text-[10px] font-bold tracking-[1.5px] uppercase text-[#8C98B0]',
        className,
      )}
    >
      {children}
    </label>
  );
}

// ─── CARD ────────────────────────────────────────────────────────────────────
interface CardProps {
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
  padding?: boolean;
}

export function Card({ children, className, onClick, padding = true }: CardProps) {
  return (
    <div
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      className={cn(
        'bg-[#131C30] rounded-2xl border border-[rgba(255,255,255,0.07)]',
        padding && 'p-4',
        onClick && 'cursor-pointer hover:border-[rgba(255,255,255,0.14)] transition-all hover:-translate-y-px',
        className,
      )}
    >
      {children}
    </div>
  );
}

// ─── SPINNER ─────────────────────────────────────────────────────────────────
export function Spinner({ className }: { className?: string }) {
  return (
    <svg
      className={cn('animate-spin h-4 w-4 text-current', className)}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  );
}

// ─── BADGE ───────────────────────────────────────────────────────────────────
interface BadgeProps {
  children: React.ReactNode;
  color?: 'orange' | 'blue' | 'yellow' | 'dim' | 'green';
  className?: string;
}

const BADGE_COLOR: Record<NonNullable<BadgeProps['color']>, string> = {
  orange: 'bg-[#E8441A] text-white',
  blue:   'bg-[#4B8EDB] text-white',
  yellow: 'bg-[#F5A623] text-black',
  dim:    'bg-[rgba(255,255,255,0.08)] text-[#8C98B0]',
  green:  'bg-emerald-600 text-white',
};

export function Badge({ children, color = 'dim', className }: BadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 h-5 px-2 rounded-full',
        'text-[10px] font-bold tracking-[0.6px] uppercase',
        BADGE_COLOR[color],
        className,
      )}
    >
      {children}
    </span>
  );
}

// ─── CHIP ────────────────────────────────────────────────────────────────────
interface ChipProps {
  children: React.ReactNode;
  active?: boolean;
  onClick?: () => void;
  icon?: React.ReactNode;
  className?: string;
}

export function Chip({ children, active, onClick, icon, className }: ChipProps) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full',
        'text-[13px] font-medium border whitespace-nowrap flex-shrink-0',
        'transition-all duration-150 cursor-pointer',
        active
          ? 'border-[#F4F3F0] bg-[#F4F3F0] text-[#0A0F1C]'
          : 'border-[rgba(255,255,255,0.14)] bg-transparent text-[#F4F3F0] hover:border-[rgba(255,255,255,0.3)]',
        className,
      )}
    >
      {icon}
      {children}
    </button>
  );
}

// ─── TAG ─────────────────────────────────────────────────────────────────────
interface TagProps {
  children: React.ReactNode;
  color?: string;
  className?: string;
}

export function Tag({ children, color, className }: TagProps) {
  return (
    <span
      style={{ background: color ?? T.ink }}
      className={cn(
        'inline-flex items-center gap-1 h-[22px] px-2 rounded-full',
        'text-[10px] font-bold tracking-[0.6px] uppercase text-white',
        className,
      )}
    >
      {children}
    </span>
  );
}

// ─── SHIMMER SKELETON ─────────────────────────────────────────────────────────
export function Shimmer({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        'shimmer rounded-md',
        className,
      )}
    />
  );
}

// ─── EMPTY STATE ─────────────────────────────────────────────────────────────
export function EmptyState({
  title = 'Nothing here yet',
  body,
  action,
}: {
  title?: string;
  body?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center py-16 gap-4 text-center">
      <div className="text-4xl">✈</div>
      <h3 className="font-semibold text-lg text-[#F4F3F0]">{title}</h3>
      {body && <p className="text-sm text-[#8C98B0] max-w-xs">{body}</p>}
      {action}
    </div>
  );
}

// ─── ERROR STATE ──────────────────────────────────────────────────────────────
export function ErrorState({
  title = 'Something went wrong',
  body,
  onRetry,
}: {
  title?: string;
  body?: string;
  onRetry?: () => void;
}) {
  return (
    <div className="flex flex-col items-center justify-center py-16 gap-4 text-center">
      <div className="text-4xl">⚠</div>
      <h3 className="font-semibold text-lg text-[#F4F3F0]">{title}</h3>
      {body && <p className="text-sm text-[#8C98B0] max-w-xs">{body}</p>}
      {onRetry && (
        <Button variant="ghost" size="sm" onClick={onRetry}>
          Try again
        </Button>
      )}
    </div>
  );
}

// ─── MODAL (Radix Dialog) ────────────────────────────────────────────────────
interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  width?: string;
}

export function Modal({ open, onClose, title, children, width = '640px' }: ModalProps) {
  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm animate-fade-up" />
        <Dialog.Content
          style={{ maxWidth: width }}
          className={cn(
            'fixed z-50 w-full left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2',
            'bg-[#131C30] rounded-2xl border border-[rgba(255,255,255,0.14)]',
            'shadow-[0_40px_100px_rgba(0,0,0,0.6)]',
            'p-6 focus:outline-none',
          )}
        >
          {title && (
            <div className="flex items-center justify-between mb-4">
              <Dialog.Title className="text-lg font-bold text-[#F4F3F0]">{title}</Dialog.Title>
              <Dialog.Close asChild>
                <button
                  onClick={onClose}
                  className="w-8 h-8 rounded-lg bg-[#0A0F1C] border border-[rgba(255,255,255,0.07)] flex items-center justify-center text-[#8C98B0] hover:text-[#F4F3F0] transition-colors"
                >
                  <X size={14} />
                </button>
              </Dialog.Close>
            </div>
          )}
          {children}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// ─── NOT-YET-WIRED TOAST ──────────────────────────────────────────────────────
// Fires when the user clicks a CTA that has no BE wiring yet.
// The message parameter names the upcoming PR that will wire it.
export function notYetWiredToast(feature: string) {
  toast.info(`${feature} is coming soon`, {
    description: `This lands with the ${feature} PR. Stay tuned!`,
    duration: 4000,
  });
}
